import { useState, useRef, useCallback, useEffect } from "react"
import CMSData from "aldoo-ra/CMS/cms-data"
import { createPortal } from "react-dom"

function Lightbox({ src, alt, onClose }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Trigger fade-in on next frame
    const id = requestAnimationFrame(() => setVisible(true))
    const onKey = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", onKey)
    return () => {
      cancelAnimationFrame(id)
      document.removeEventListener("keydown", onKey)
    }
  }, [onClose])

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
    >
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-full rounded-2xl object-contain select-none cursor-zoom-out"
        draggable={false}
      />
    </div>,
    document.body
  )
}

export default function ImageGallery({
  id,
  className = "",
  showDots = true,
  rounded = "rounded-2xl",
  objectFit = "object-contain",
  aspectRatio = "16/9",
  alt = "",
}) {
  const { data = [] } = CMSData({ contentType: "ImageGallery", match: { "content.id": id } })
  const item = data[0]

  const images = item
    ? (Array.isArray(item.images) ? item.images : [])
        .map((img) => (typeof img === "string" ? img : img?.url))
        .filter(Boolean)
    : []

  const count = images.length

  const [pos, setPos] = useState(1)
  const [animated, setAnimated] = useState(true)
  const [lightboxSrc, setLightboxSrc] = useState(null)
  const touchStartX = useRef(null)
  const isTransitioning = useRef(false)

  const realIndex = Math.max(0, Math.min(count - 1, pos - 1))

  const goToSlot = useCallback((slot, anim = true) => {
    if (anim) isTransitioning.current = true
    setAnimated(anim)
    setPos(slot)
  }, [])

  const next = useCallback(() => {
    if (isTransitioning.current) return
    goToSlot(pos + 1)
  }, [pos, goToSlot])

  const prev = useCallback(() => {
    if (isTransitioning.current) return
    goToSlot(pos - 1)
  }, [pos, goToSlot])

  const goToReal = useCallback((i) => {
    if (isTransitioning.current) return
    goToSlot(i + 1)
  }, [goToSlot])

  const handleTransitionEnd = useCallback(() => {
    isTransitioning.current = false
    if (pos === 0) {
      setAnimated(false)
      setPos(count)
    } else if (pos === count + 1) {
      setAnimated(false)
      setPos(1)
    }
  }, [pos, count])

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return
    const delta = e.changedTouches[0].clientX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(delta) < 40) return
    if (delta < 0) next()
    else prev()
  }

  if (!images.length) return null

  if (count === 1) {
    return (
      <>
        <div className={`w-full overflow-hidden ${rounded} ${className}`} style={{ aspectRatio }}>
          <img
            src={images[0]}
            alt={alt}
            className={`w-full h-full ${objectFit} ${rounded} cursor-zoom-in`}
            onClick={() => setLightboxSrc(images[0])}
            draggable={false}
          />
        </div>
        {lightboxSrc && <Lightbox src={lightboxSrc} alt={alt} onClose={() => setLightboxSrc(null)} />}
      </>
    )
  }

  const slots = [images[count - 1], ...images, images[0]]
  const totalSlots = slots.length

  return (
    <>
      <div className={`flex flex-col gap-3 ${className}`}>
        <div
          className={`relative w-full overflow-hidden ${rounded} select-none`}
          style={{ aspectRatio }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="absolute inset-0 flex"
            style={{
              width: `${totalSlots * 100}%`,
              transform: `translateX(-${(pos / totalSlots) * 100}%)`,
              transition: animated ? "transform 300ms ease-in-out" : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {slots.map((src, i) => (
              <div key={i} className="relative h-full" style={{ width: `${100 / totalSlots}%` }}>
                <img
                  src={src}
                  alt={alt || `Slide ${i}`}
                  className={`absolute inset-0 w-full h-full ${objectFit} ${rounded} cursor-zoom-in`}
                  draggable={false}
                  onClick={() => setLightboxSrc(src)}
                />
              </div>
            ))}
          </div>

          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition"
            aria-label="Previous"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition"
            aria-label="Next"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {showDots && (
          <div className="flex items-center justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => goToReal(i)}
                className={`rounded-full transition-all duration-200 ${
                  i === realIndex
                    ? "w-4 h-2 bg-primary"
                    : "w-2 h-2 bg-bordered hover:bg-text_secondary"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {lightboxSrc && <Lightbox src={lightboxSrc} alt={alt} onClose={() => setLightboxSrc(null)} />}
    </>
  )
}
